import * as React from "react";
import EmailTemplate from "./commons/model/EmailTemplate";
import Form from "./commons/components/form/Form";
import TextInput from "./commons/components/form/input/TextInput";
import RichTextEditorInput from "./commons/components/form/input/RichTextEditorInput";
import ModalPopup from "./commons/components/modal/ModalPopup";
import InputState from "./commons/model/form/InputState";
import { maxLength, required } from "./commons/model/form/Validators";
import sfetch from "./commons/utils/super-fetch";
import { createRoot } from "react-dom/client";
import { sanitize } from "./commons/utils/sanitize";
import { decode } from "html-entities";

interface EmailTemplateEditorProps {
  template?: EmailTemplate;
  createApiUrl?: string;
  cssUrl: string;
  videoConsultationUrl?: string;
  bookingAppointmentUrl?: string;
  emailContentMaxLength: number;
  emailSubjectMaxLength: number;
}

interface EmailTemplateEditorState {
  name?: string;
}

export default class EmailTemplateEditor extends React.Component<
  EmailTemplateEditorProps,
  EmailTemplateEditorState
> {
  private rteHeight = "35rem";

  constructor(props: EmailTemplateEditorProps) {
    super(props);
    this.save = this.save.bind(this);
    this.state = {};
  }

  render() {
    const template = this.props.template;
    return (
      <ModalPopup
        closeModal={() => this.closeModal(false)}
        fullscreen={true}
        class="editor"
        id="email-template-editor"
      >
        {this.props.createApiUrl ? (
          <div className="title">
            {this.state.name || i18n["Page.Catalog.EmailTemplates.Create"]}
          </div>
        ) : (
          <div className="title">
            {i18n["Global.Edit"]} {this.state.name}
          </div>
        )}
        <Form submitLabel={i18n["Global.Save"]} submit={this.save}>
          <TextInput
            name="name"
            label={i18n["EmailTemplateEditor.Name.Title"]}
            initialValue={template?.name}
            validators={[required()]}
            onChange={(inputState) => this.setState({ name: inputState.value })}
          />
          <TextInput
            name="subject"
            label={i18n["EmailTemplateEditor.Subject.Title"]}
            initialValue={sanitize(decode(template?.subject))}
            validators={[required(), maxLength("", this.props.emailSubjectMaxLength)]}
          />
          {/* class HtmlSanitizer must be aligned with WYSIWYG editor for allowed html tags and attributes */}
          <RichTextEditorInput
            name="content"
            label={i18n["EmailTemplateEditor.Content.Title"]}
            initialValue={template?.content}
            cssUrl={this.props.cssUrl}
            height={this.rteHeight}
            maxSize={this.props.emailContentMaxLength}
            quickLinks={{
              bookingAppointmentUrl: this.props.bookingAppointmentUrl,
              videoConsultationUrl: this.props.videoConsultationUrl,
            }}
            validators={[required()]}
          />
        </Form>
      </ModalPopup>
    );
  }

  componentDidMount() {
    window.onbeforeunload = this.onClose;
  }

  componentWillUnmount(): void {
    window.onbeforeunload = null;
  }

  private onClose() {
    return "prevent-data-lose";
  }

  private closeModal(saved: boolean) {
    let path = document.location.pathname.split("/");
    path.pop();
    const hash = "#" + encodeURI(saved ? this.state.name : (this.props.template?.name ?? ""));
    document.location.href = path.join("/") + hash;
  }

  private async save(form: { [key: string]: InputState }) {
    const payload = {
      name: form.name.value,
      subject: form.subject.value,
      content: form.content.value,
      category: this.props.template?.category,
    };

    await sfetch(this.props.createApiUrl || this.props.template.apiUrl, {
      method: "POST",
      body: JSON.stringify(payload),
      headers: {
        "Content-Type": "application/json",
      },
    });
    this.componentWillUnmount();
    this.closeModal(true);
  }

  static loadComponent(elementId: string, props: EmailTemplateEditorProps) {
    const container = document.getElementById(elementId);
    const root = createRoot(container!);
    root.render(<EmailTemplateEditor {...props} />);
  }
}
